import { createAction } from '@reduxjs/toolkit';
import type { RootState } from 'app/store';
import {
  HYDRATABLE_SLICES,
  MAX_SNAPSHOT_BYTES,
  SNAPSHOT_DROP_ORDER,
  SNAPSHOT_SCHEMA_VERSION
} from 'data/error-log-constants';
import { HydratableSliceType, ReduxSnapshotType } from 'types/error-log';
import { isCompatibleReduxSnapshot } from 'utils/error-log/is-compatible-redux-snapshot';
import { mergeHydratableSlices } from 'utils/error-log/merge-hydratable-slices';
import { restoreSessionUi } from 'utils/error-log/restore-session-ui';
import { collectSnapshotSliceValues } from 'utils/error-log/snapshot-slice-values';

export const hydrateReduxSnapshot = createAction<ReduxSnapshotType>(
  'errorLog/hydrateReduxSnapshot'
);

function estimateBytes(value: unknown): number {
  const serialized = JSON.stringify(value);
  return serialized ? serialized.length : 0;
}

export function selectSlicesToDrop(
  sizes: Record<HydratableSliceType, number>,
  maxBytes: number = MAX_SNAPSHOT_BYTES
): HydratableSliceType[] {
  let total = Object.values(sizes).reduce((sum, size) => sum + size, 0);
  const dropped: HydratableSliceType[] = [];
  for (const key of SNAPSHOT_DROP_ORDER) {
    if (total <= maxBytes) {
      return dropped;
    }
    total -= sizes[key];
    dropped.push(key);
  }
  return dropped;
}

export function buildReduxSnapshot(
  state: RootState,
  maxBytes: number = MAX_SNAPSHOT_BYTES
): ReduxSnapshotType {
  const values = collectSnapshotSliceValues(state);
  const sizes = Object.fromEntries(
    HYDRATABLE_SLICES.map((key) => [key, estimateBytes(values[key])])
  ) as Record<HydratableSliceType, number>;
  const droppedSlices = selectSlicesToDrop(sizes, maxBytes);
  const slices = Object.fromEntries(
    HYDRATABLE_SLICES.filter((key) => !droppedSlices.includes(key)).map(
      (key) => [key, values[key]]
    )
  ) as ReduxSnapshotType['slices'];

  return {
    slices,
    meta: {
      schemaVersion: SNAPSHOT_SCHEMA_VERSION,
      capturedAt: Date.now(),
      droppedSlices
    }
  };
}

export function mergeReduxSnapshot(
  state: RootState,
  snapshot: ReduxSnapshotType
): RootState {
  return mergeHydratableSlices(state, snapshot.slices);
}

export function applyReduxSnapshot(
  state: RootState,
  snapshot: ReduxSnapshotType
): RootState | null {
  if (!isCompatibleReduxSnapshot(snapshot)) {
    return null;
  }
  return restoreSessionUi(state, mergeReduxSnapshot(state, snapshot));
}
